// cleanup_remove_external_libraries_TRANSFERS
// cleanup_convert_to_typescript_file_TRANSFERS

import { memoize } from 'lodash';
import axios from 'axios';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  memoizeWithToken,
  setTokenAsCacheKey,
  setTokenAndParamsAsCacheKey
} from '../utils/memoizeUtils';
import { addAwaitingApproval } from './mappers';
/* istanbul ignore file */

export function fetchTransferData(postData) {
  // INTEGRATION TODO:
  // Add in authentication when we can test.
  return from(axios.post('/transfer/getList', postData)).pipe(map(({ data: { data } }) => data));
}

export function approveTransfers(transferIds, observe) {
  const postData = {
    transferIds
  };
  if (observe) {
    return from(axios.post('/transfer/approve', postData)).pipe(map(({ data: { data } }) => data));
  }
  return axios.post('/transfer/approve', postData).then(({ data: { data } }) => data);
}
export function getNumberAmount(amount) {
  return amount.replace(/[^0-9.-]+/g, '');
}

export function getRequestParams(transfer, flag) {
  const amt = transfer.amount;
  return {
    amount: amt.toString().indexOf(',') > -1 ? getNumberAmount(amt) : amt,
    creditAccountId: transfer.creditAccountId,
    debitAccountId: transfer.debitAccountId,
    memo: transfer.memo,
    processingDate: transfer.processingDate.format(),
    templateName: transfer.templateName === '' || flag ? null : transfer.templateName,
    templateId: transfer.templateId === '' ? null : transfer.templateId
  };
}

export function createTransfer(postData) {
  const newPostData = Array.isArray(postData) ? postData : [postData];

  return from(axios.post('/transfer/save', newPostData))
    .pipe(map(({ data: { data, success } }) => ({ data, success })))
    .toPromise();
}

export function deleteTransfers(ids) {
  const postData = { ids };
  return from(axios.post('/transfer/delete', postData)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export function createTransferTemplate(postData) {
  return from(axios.post('/transfer/template/create', postData))
    .pipe(
      map(({ data: { data, success } }) => ({ data, success })),
      map(addAwaitingApproval)
    )
    .toPromise();
}

export function modifyTransferTemplate(postData) {
  return from(axios.post('/transfer/template/update', postData))
    .pipe(
      map(({ data: { data, success } }) => ({ data, success })),
      map(addAwaitingApproval)
    )
    .toPromise();
}

export function rejectTransfers(transferIds) {
  const postData = {
    transferIds
  };
  return axios.post('/transfer/reject', postData).then(({ data: { data } }) => data);
}

export function retrievePermissions(postData) {
  return from(axios.post('/transfer/getPermissions', postData)).pipe(
    map(({ data: { data } }) => data)
  );
}
export const getPermittedAccounts = memoize(
  (postData) => {
    return from(axios.post('/transfer/getPermittedAccounts', postData)).pipe(
      map(({ data: { data } }) => data)
    );
  },
  (postData) => setTokenAndParamsAsCacheKey(postData)
);

export const getAllowedTransferDates = memoize(
  () => from(axios.post('/transfer/getCalendar')).pipe(map(({ data: { data } }) => data)),
  setTokenAsCacheKey
);

export function getRecurringTransfersData(postData) {
  return from(axios.post('/transfer/instruction/getList', postData)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function deleteRecurringTransfers(transferIds) {
  const postData = {
    transferIds
  };
  return axios.post('/transfer/instruction/delete', postData).then(({ data: { data } }) => data);
}

export function getSingleTransfer(postData) {
  return from(axios.post('/transfer/retrieve', postData)).pipe(map(({ data: { data } }) => data));
}

export function getTransferTemplates(postData) {
  return from(axios.post('/transfer/template/getList', postData)).pipe(
    map(({ data: { data } }) => data)
  );
}

export const getTransferTemplateSettings = memoizeWithToken(() =>
  from(axios.post('/transfer/template/getSettings')).pipe(map(({ data: { data } }) => data))
);

export function deleteTransferTemplates(postData) {
  return from(axios.post('/transfer/template/delete', postData)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getSingleTemplate(postData) {
  return from(axios.post('/transfer/template/getDetails', postData)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function getTransferInstruction(payload) {
  return from(axios.post(`/transfer/instruction/retrieve`, payload)).pipe(
    map(({ data: { data } }) => data)
  );
}

export function createTransferInstruction(postData) {
  return axios
    .post('/transfer/instruction/create', postData)
    .then(({ data: { data, success } }) => ({
      data,
      success
    }));
}

export const modifyTransferInstruction = (postData) =>
  axios.post('/transfer/instruction/modify', postData).then(({ data: { data, success } }) => ({
    data,
    success
  }));

export const getTransferImportDefaultFields = () =>
  from(axios.post('/transfer/getTransferImportDefaultFields')).pipe(
    map(({ data: { data } }) => data)
  );

export const updateTransferImportMap = (payload) =>
  from(axios.post('/transfer/createTransferImportMap', payload)).pipe(map(({ data }) => data));

export const createTransferImportMap = (payload) =>
  from(axios.post('/transfer/createTransferImportMap', payload)).pipe(map(({ data }) => data));

export const getDelimiters = () =>
  from(axios.post('/transfer/getDelimiters')).pipe(map(({ data: { data } }) => data));

export const listTransferImportMaps = () =>
  from(axios.post('/transfer/listTransferImportMaps')).pipe(map(({ data: { data } }) => data));

export const findTransferImportMap = (params) =>
  from(axios.post('/transfer/findTransferImportMap', params)).pipe(
    map(({ data: { data } }) => data)
  );

export const deleteTransferImportMap = (params) =>
  from(axios.post('/transfer/deleteTransferImportMaps', params)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export const validateName = (name) =>
  from(axios.post('/transfer/instruction/validateName', { name })).pipe(
    map(({ data: { data } }) => data)
  );

export const transferSummary = (payload) => () =>
  from(axios.post('/transfer/transferSummary', payload)).pipe(map(({ data: { data } }) => data));

export const uploadFile = ({ params, payload }) =>
  from(
    axios.post('/transfer/uploadFile', payload, {
      params,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  ).pipe(map(({ data: { data, success } }) => ({ data, success })));

export const listTransferImports = (params) => () =>
  from(axios.post('/transfer/listTransferImports', params)).pipe(
    map(({ data: { data } }) => data)
  );

export const approveBkImport = (params) => () =>
  from(axios.post('/transfer/approveBkImport', params)).pipe(map(({ data }) => data));

export const cancelBkImport = (postData) =>
  from(axios.post('/transfer/cancelBkImport', postData)).pipe(map(({ data }) => data));

export const getTransferLimits = (postData) =>
  from(axios.post('/transfer/limits/get', postData)).pipe(map(({ data }) => data));
